import { canPlayOnArm } from '../../game/Chain.js';
import {
  applyDraw,
  applyMove,
  applyPass,
  claimMuggins,
  type GameStateData,
} from '../../game/GameState.js';
import { hasLegalPlay } from '../../game/MoveValidator.js';
import { getScoringPolicy } from '../../game/scoring/index.js';
import { tileEquals } from '../../game/types.js';
import { BeliefTracker } from '../BeliefTracker.js';
import { Determinizer, applyDeterminization } from '../Determinizer.js';
import { MoveEnumerator } from '../MoveEnumerator.js';
import { scorerFor } from '../scorerFor.js';
import type { AiAction, MoveScorer } from '../types.js';
import type { DominoRuleSet } from '../../game/variants/types.js';
import { actionKey, decodePlayKey, type ActionKey } from './action.js';
import { Node } from './Node.js';
import { runRollout, terminalReward } from './rollout.js';

/**
 * SO-IS-MCTS search per Cowling/Powley/Whitehouse 2012. Single-observer:
 * the search runs from the AI's view; opponent decisions inside the tree
 * are modeled with the same UCB1 selection but only consider actions legal
 * in the *current iteration's* determinization. The tree persists across
 * iterations — only the determinization changes — so the visit counts
 * reflect aggregated experience across many sampled worlds.
 */

export interface IsmctsOptions {
  readonly ruleSet: DominoRuleSet;
  readonly iterations: number;
  /** Optional wall-clock cap in ms. The loop exits whichever ends first. */
  readonly deadlineMs?: number;
  /** UCB1 exploration constant. */
  readonly c?: number;
  readonly rng?: () => number;
}

const DEFAULT_C = Math.SQRT2;

export class IsmctsSearcher {
  private readonly enumerator: MoveEnumerator;
  private readonly evaluator: MoveScorer;
  private readonly rewardSign: 1 | -1;
  private readonly determinizer: Determinizer;
  private readonly rng: () => number;
  private readonly c: number;

  constructor(private readonly opts: IsmctsOptions) {
    this.enumerator = new MoveEnumerator(opts.ruleSet);
    this.evaluator = scorerFor(opts.ruleSet);
    this.rewardSign = getScoringPolicy(opts.ruleSet).rewardSign;
    this.rng = opts.rng ?? Math.random;
    this.determinizer = new Determinizer({ rng: this.rng });
    this.c = opts.c ?? DEFAULT_C;
  }

  /**
   * Run the iteration budget and return the best action plus the count of
   * iterations actually completed. The root state is the AI's information
   * set — the AI's own hand is fixed; opponent hands + boneyard are sampled
   * fresh on every iteration.
   */
  search(state: GameStateData, rootSeat: number): { action: AiAction; iterations: number } {
    const tracker = new BeliefTracker(state, rootSeat);
    const root = new Node(rootSeat);
    const deadline = this.opts.deadlineMs ? Date.now() + this.opts.deadlineMs : Infinity;
    let i = 0;
    for (; i < this.opts.iterations; i += 1) {
      if (i % 64 === 0 && Date.now() > deadline) break;
      const world = this.determinizer.sample(state, rootSeat, tracker);
      const initial = applyDeterminization(state, world);
      this.iterate(initial, root, rootSeat);
    }
    const bestKey = root.bestAction();
    if (bestKey === null) {
      return { action: fallbackAction(state), iterations: i };
    }
    return { action: keyToAction(bestKey), iterations: i };
  }

  private iterate(initialState: GameStateData, root: Node, rootSeat: number): void {
    const startScores = initialState.scores;
    const teamOf = initialState.teamMap.teamOf;
    let state = settle(initialState);
    let node: Node | null = root;
    const path: Array<{ node: Node; key: ActionKey; legal: readonly ActionKey[] }> = [];

    // Selection: descend the tree while every legal action at the current
    // node has been visited at least once. Bump availability for every
    // legal edge to keep UCB1 balanced. (`node.actor` always equals
    // `state.currentSeat` because the actor sequence is deterministic
    // given the chosen action sequence — every child node is created with
    // `actor = state.currentSeat` after the parent edge's transition.)
    while (!state.handOver && node !== null) {
      const legal = legalActionKeys(state);
      if (legal.length === 0) break;
      node.bumpAvailability(legal);
      const untried = node.untriedLegal(legal);
      if (untried.length > 0) {
        // Heuristic-guided expansion: pick the untried action with the
        // highest Medium-tier evaluator score. Random expansion biases
        // toward exploring uniformly across noisy candidates; evaluator-
        // ordered expansion concentrates early visits on plays that the
        // 1-ply heuristic already likes. Big win at low iteration counts.
        const expandKey = pickBestByEval(state, untried, this.evaluator, this.enumerator);
        const edge = node.getOrCreateEdge(expandKey);
        state = applyKey(state, state.currentSeat, expandKey);
        state = settle(state);
        const childActor = state.handOver ? node.actor : state.currentSeat;
        const child = new Node(childActor);
        edge.child = child;
        path.push({ node, key: expandKey, legal });
        node = null;
        break;
      }
      const signFromRoot: 1 | -1 = sameTeam(initialState.teamMap.teamOf, node.actor, rootSeat)
        ? 1
        : -1;
      const pick = node.pickUct(legal, this.c, signFromRoot);
      if (pick === null) break;
      const edge = node.getOrCreateEdge(pick);
      state = applyKey(state, state.currentSeat, pick);
      state = settle(state);
      path.push({ node, key: pick, legal });
      node = edge.child;
    }

    // Rollout to terminal.
    if (!state.handOver) {
      state = runRollout(state, {
        evaluator: this.evaluator,
        enumerator: this.enumerator,
        rng: this.rng,
      });
    }

    const reward = terminalReward(startScores, state.scores, teamOf, rootSeat, this.rewardSign);

    // Backprop: update visited edges. UCB1 from rootSeat's perspective —
    // every node's reward is from root's view, since IS-MCTS uses a single
    // observer; opponent nodes still get reward credited from root's view.
    for (const step of path) {
      const edge = step.node.getOrCreateEdge(step.key);
      edge.visits += 1;
      edge.totalReward += reward;
      step.node.visits += 1;
    }
  }
}

function sameTeam(teamOf: readonly number[], a: number, b: number): boolean {
  return (teamOf[a] ?? a) === (teamOf[b] ?? b);
}

function pickBestByEval(
  state: GameStateData,
  untried: readonly ActionKey[],
  evaluator: MoveScorer,
  enumerator: MoveEnumerator,
): ActionKey {
  let best = untried[0]!;
  let bestScore = -Infinity;
  const seat = state.currentSeat;
  const hand = state.hands[seat] ?? [];
  const moves = enumerator.enumerate(state.chain, hand);
  const byKey = new Map<ActionKey, number>();
  for (const m of moves) {
    const key =
      m.armIndex < 0
        ? `lead:${m.tile[0]}-${m.tile[1]}`
        : `play:${m.tile[0]}-${m.tile[1]}:${m.armIndex}`;
    byKey.set(key, evaluator.score(state, seat, m));
  }
  for (const k of untried) {
    const score = byKey.get(k) ?? -Infinity;
    if (score > bestScore) {
      bestScore = score;
      best = k;
    }
  }
  return best;
}

function legalActionKeys(state: GameStateData): readonly ActionKey[] {
  const out: ActionKey[] = [];
  const seat = state.currentSeat;
  const hand = state.hands[seat] ?? [];
  if (hand.length > 0 && hasLegalPlay(state.chain, hand, state.forcedLeadTile)) {
    if (state.chain.lead === null) {
      // Телефон forced-lead: only the forced tile is a legal lead.
      const candidates = state.forcedLeadTile
        ? hand.filter((t) => tileEquals(t, state.forcedLeadTile!))
        : hand;
      for (const tile of candidates) out.push(`lead:${tile[0]}-${tile[1]}`);
    } else {
      const armIndices = state.chain.arms.map((_, i) => i);
      for (const tile of hand) {
        for (const armIdx of armIndices) {
          // canPlayOnArm handles the double-outer match (face, not 2×face).
          if (canPlayOnArm(state.chain, armIdx, tile)) {
            out.push(`play:${tile[0]}-${tile[1]}:${armIdx}`);
          }
        }
      }
    }
    return out;
  }
  if (state.boneyard.length > 0 && state.ruleSet.drawRule !== 'no-draw') {
    out.push('draw');
  } else {
    out.push('pass');
  }
  return out;
}

function applyKey(state: GameStateData, seat: number, key: ActionKey): GameStateData {
  if (key === 'draw') return applyDraw(state, seat);
  if (key === 'pass') return applyPass(state, seat);
  if (key === 'claim') {
    return state.pendingMuggins ? claimMuggins(state, state.pendingMuggins.scorerSeat, Date.now()) : state;
  }
  const decoded = decodePlayKey(key);
  if (decoded === null) return state;
  return applyMove(state, seat, decoded.tile, decoded.armIndex, Date.now()).state;
}

function settle(state: GameStateData): GameStateData {
  if (!state.pendingMuggins) return state;
  return claimMuggins(state, state.pendingMuggins.scorerSeat, Date.now());
}

function keyToAction(key: ActionKey): AiAction {
  if (key === 'draw') return { kind: 'draw' };
  if (key === 'pass') return { kind: 'pass' };
  if (key === 'claim') return { kind: 'claim-muggins' };
  const decoded = decodePlayKey(key);
  if (decoded === null) return { kind: 'pass' };
  return { kind: 'play', tile: decoded.tile, armIndex: decoded.armIndex };
}

function fallbackAction(state: GameStateData): AiAction {
  if (state.boneyard.length > 0 && state.ruleSet.drawRule !== 'no-draw') return { kind: 'draw' };
  if (state.chain.lead === null) {
    const first = state.hands[state.currentSeat]?.[0];
    if (first) return { kind: 'play', tile: first, armIndex: -1 };
  }
  return { kind: 'pass' };
}

export { actionKey };
